import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CommissionContractOffersAPI, { CommissionContractOfferDto } from '../api/CommissionContractOffersAPI';
import { toastService } from '../services/ToastService';
import { getDefaultCurrency } from '../utils/currencyDetection';
import { Settings } from '../store/reducers/AccountSlice';
import { CommissionContractTemplate } from '../types/CommissionContractTemplate';

export const DEFAULT_SALON_SPLIT_PERCENTAGE = 20;
export const DEFAULT_BRIDE_SPLIT_PERCENTAGE = 80;

export function createEmptyOffer(
  settings: Pick<Settings, 'currency' | 'isLoggedIn'>,
): CommissionContractOfferDto {
  const currency = getDefaultCurrency(undefined, settings?.currency, settings?.isLoggedIn);
  return {
    name: '',
    customerDescription: '',
    saleDuration: 180,
    salonSplitPercentage: DEFAULT_SALON_SPLIT_PERCENTAGE,
    brideSplitPercentage: DEFAULT_BRIDE_SPLIT_PERCENTAGE,
    dropFeePrice: 0,
    dropFeeCurrency: currency,
    renewalFeePrice: 0,
    renewalFeeCurrency: currency,
    enabled: true,
  };
}

export function createDefaultOfferFromTemplate(
  template: Pick<
    CommissionContractTemplate,
  'saleDuration' | 'salonSplitPercentage' | 'brideSplitPercentage' | 'dropFee' | 'renewalFee'
  >,
  settings: Pick<Settings, 'currency' | 'isLoggedIn'>,
  defaultName: string,
): CommissionContractOfferDto {
  const storeCurrency = getDefaultCurrency(undefined, settings?.currency, settings?.isLoggedIn);
  const dropCurrency = template.dropFee?.currency?.trim() || storeCurrency;
  const renewalCurrency = template.renewalFee?.currency?.trim() || storeCurrency;

  return {
    name: defaultName,
    customerDescription: '',
    saleDuration: template.saleDuration > 0 ? template.saleDuration : 180,
    salonSplitPercentage: template.salonSplitPercentage ?? DEFAULT_SALON_SPLIT_PERCENTAGE,
    brideSplitPercentage: template.brideSplitPercentage ?? DEFAULT_BRIDE_SPLIT_PERCENTAGE,
    dropFeePrice: template.dropFee?.price ?? 0,
    dropFeeCurrency: dropCurrency,
    renewalFeePrice: template.renewalFee?.price ?? 0,
    renewalFeeCurrency: renewalCurrency,
    enabled: true,
  };
}

interface UseCommissionOfferEditorOptions {
  onSaveSuccess?: () => void | Promise<void>;
}

export interface UseCommissionOfferEditorResult {
  editOpen: boolean;
  formData: CommissionContractOfferDto;
  setFormData: (offer: CommissionContractOfferDto) => void;
  isSaving: boolean;
  isCreatingFirst: boolean;
  openNewOffer: () => void;
  openEditOffer: (offer: CommissionContractOfferDto) => void;
  createFirstOfferQuick: (template: CommissionContractTemplate) => Promise<void>;
  closeEditor: () => void;
  handleSave: () => Promise<void>;
  setEditOpen: (open: boolean) => void;
}

export function useCommissionOfferEditor(
  options: UseCommissionOfferEditorOptions = {},
): UseCommissionOfferEditorResult {
  const { onSaveSuccess } = options;
  const { t } = useTranslation();
  const settings: Settings = useSelector((state: any) => state.settings.value);

  const emptyOffer = useCallback(
    () => createEmptyOffer(settings),
    [settings],
  );

  const [editOpen, setEditOpen] = useState(false);
  const [formData, setFormData] = useState<CommissionContractOfferDto>(() => createEmptyOffer(settings));
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingFirst, setIsCreatingFirst] = useState(false);

  const openNewOffer = useCallback(() => {
    setFormData(emptyOffer());
    setEditOpen(true);
  }, [emptyOffer]);

  const openEditOffer = useCallback((offer: CommissionContractOfferDto) => {
    setFormData({ ...offer });
    setEditOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    setEditOpen(false);
    setFormData(emptyOffer());
  }, [emptyOffer]);

  const createFirstOfferQuick = useCallback(
    async (template: CommissionContractTemplate) => {
      const defaultName = t('commission_payment_offers.default_name') || 'Standard';
      const offer = createDefaultOfferFromTemplate(template, settings, defaultName);
      setIsCreatingFirst(true);
      try {
        await CommissionContractOffersAPI.create(offer);
        toastService.showToast(
          t('commission_payment_offers.create_first_success') || 'Standard offer created. You can edit it anytime.',
          'success',
        );
        await onSaveSuccess?.();
      } catch (error) {
        console.error('Failed to create first commission contract offer', { error });
        toastService.showToast(
          t('commission_payment_offers.create_first_error') || 'Could not create offer. Please try again.',
          'error',
        );
      } finally {
        setIsCreatingFirst(false);
      }
    },
    [onSaveSuccess, settings, t],
  );

  const handleSave = useCallback(async () => {
    if (!formData.name?.trim()) {
      toastService.showToast(
        t('commission_payment_offers.name_required') || 'Name is required',
        'error',
      );
      return;
    }
    setIsSaving(true);
    try {
      if (formData.id == null) {
        await CommissionContractOffersAPI.create(formData);
      } else {
        await CommissionContractOffersAPI.update(formData.id, formData);
      }
      toastService.showToast(t('commission_payment_offers.saved') || 'Saved', 'success');
      setEditOpen(false);
      setFormData(emptyOffer());
      await onSaveSuccess?.();
    } catch (error) {
      console.error('Failed to save commission contract offer', { offerId: formData.id, error });
      toastService.showToast(t('commission_payment_offers.save_error') || 'Save failed', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [formData, emptyOffer, onSaveSuccess, t]);

  return {
    editOpen,
    formData,
    setFormData,
    isSaving,
    isCreatingFirst,
    openNewOffer,
    openEditOffer,
    createFirstOfferQuick,
    closeEditor,
    handleSave,
    setEditOpen,
  };
}
